import { isSsr } from '@lemonade-hq/cdk';
import { clampedInversedLerp, inversedLerp } from '@lemonade-hq/ts-helpers';
import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

interface Position {
  start: number;
  end: number;
  relativeStart: number;
  relativeEnd: number;
}

export type UseElementLerp = [(node?: Element | null | undefined) => void, number, Position];

export interface UseElementLerpOptions {
  startPoint?: number;
  range?: [number, number];
  debug?: boolean;
  relative?: boolean;
  clamp?: boolean;
}

const useElementLerpDebug = (debug: boolean, start: number, end: number, ttStart: number, ttEnd: number): void => {
  useEffect(() => {
    if (!debug) return;

    const elem = document.createElement('div');
    const height = end - start;
    elem.style.position = 'absolute';
    elem.style.height = `${(ttEnd - ttStart) * height}px`;
    elem.style.top = `${start + ttStart * height}px`;
    elem.style.left = '0';
    elem.style.right = '0';
    elem.style.borderTop = '1px solid green';
    elem.style.borderBottom = '1px solid red';
    elem.style.width = '100%';
    elem.style.zIndex = '100000';
    elem.style.pointerEvents = 'none';

    document.body.appendChild(elem);
    return () => {
      document.body.removeChild(elem);
    };
  }, [debug, start, end, ttEnd, ttStart]);
};

export const useElementLerp = (
  t: number,
  {
    startPoint = 0,
    range: [ttStart, ttEnd] = [0, 1],
    debug = false,
    relative = false,
    clamp = true,
  }: UseElementLerpOptions = {
    startPoint: 0,
    range: [0, 1],
    debug: false,
    relative: false,
    clamp: true,
  },
): UseElementLerp => {
  const { ref, entry } = useInView();
  const [[start, end], setRange] = useState([0, 99999]);

  const bodyHeight = isSsr() ? 99999 : document.body.clientHeight;

  useEffect(() => {
    const rect = entry?.boundingClientRect;
    if (rect == null) return;
    const windowHeight = window.innerHeight;
    const scrollTop = window.pageYOffset || window.scrollY;

    setRange([rect.top + scrollTop - startPoint * windowHeight, rect.bottom + scrollTop - startPoint * windowHeight]);
  }, [entry, startPoint]);

  useElementLerpDebug(debug, start, end, ttStart, ttEnd);

  return [
    ref,
    clamp
      ? clampedInversedLerp(
          ttStart,
          ttEnd,
          clampedInversedLerp(relative ? start / bodyHeight : start, relative ? end / bodyHeight : end, t),
        )
      : inversedLerp(
          ttStart,
          ttEnd,
          inversedLerp(relative ? start / bodyHeight : start, relative ? end / bodyHeight : end, t),
        ),
    {
      start,
      end,
      relativeStart: start / bodyHeight,
      relativeEnd: end / bodyHeight,
    },
  ];
};
